import React, { FunctionComponent } from 'react'
import { IconSizes, PaymentIcon, Span, useIsTheme, Theme, useThemeClass } from '@afterpaytouch/core'
import { BankAccountDetails } from '@afterpay/types'
import { BankAccountErrorReason } from '../../model'
import styles from './style.module.css'
import clsx from 'clsx'
import { CardAsterisk } from '../CardAsterisk'
import { useCheckoutSizes } from '../../utils/responsive'
import { useCheckoutColor } from '../../utils/color'
import { useTranslation } from '../../utils/convergence'

interface Props {
  account: BankAccountDetails | undefined
  reversed?: boolean
  iconSize?: IconSizes
  errorKey?: BankAccountErrorReason | undefined | null
  disabled?: boolean
  showErrorMessage?: boolean
}

export const PaymentMethodBankAccount: FunctionComponent<Props> = ({
  account,
  iconSize,
  errorKey,
  reversed = false,
  disabled = false,
  showErrorMessage = true,
}) => {
  const error = typeof errorKey !== 'undefined' && errorKey !== null
  const { t } = useTranslation()
  const { smallTextSize } = useCheckoutSizes()
  const themeClass = useThemeClass()
  const containerClasses = clsx(styles.container, reversed && styles.reversed)
  const accountDetailsContainerClass = clsx(styles.accountDetailsContainer, styles[themeClass])
  const { textColor, cardExpiryDateColor } = useCheckoutColor({ hasError: error, isDisabled: disabled })
  const isCashTheme = useIsTheme(Theme.Cash)
  const accountNumberColor = isCashTheme ? cardExpiryDateColor : textColor

  return (
    <span className={containerClasses}>
      <span className={styles.icon}>
        <PaymentIcon.GenericBank size={iconSize} title={t('paymentMethod:bankaccount')} />
      </span>
      <span className={accountDetailsContainerClass}>
        <span className={styles.accountDetails}>
          <Span size='M' color={textColor} bold={isCashTheme} className={styles.accountBank}>
            {account?.issuingBank}
          </Span>
          <span>
            <span className={styles.accountAsterisk}>
              <CardAsterisk error={error} disabled={disabled} color={accountNumberColor} />
            </span>
            <span className={styles.accountMaskedPan}>
              <Span size='M' color={accountNumberColor}>
                {account?.numberMask}
              </Span>
            </span>
          </span>
        </span>
        <span>
          {error && !disabled && showErrorMessage && (
            <Span size={smallTextSize} color='Fire'>
              {t('paymentMethod:bankAccountErrors.generic')}
            </Span>
          )}
        </span>
      </span>
    </span>
  )
}
